/**
 * ETL pipelines API client (ADR-0038).
 *
 * Mirrors `backend/src/hub/admin/routes/etl.py` + the descriptor model in
 * `hub/etl/descriptors.py` (camelCase aliases on the wire) and the pipeline
 * definition in `hub/etl/definition.py`.
 *
 * The block catalog drives the designer palette + per-block config form
 * (schema-driven, like the workflow catalog drives the node editor) — so a new
 * backend block appears in the UI with zero frontend change.
 *
 * Paths are relative to the `/api/admin` base (the `api` wrapper prepends it);
 * the ETL router is mounted at `/etl`.
 */
import { api } from "./api";
import type { Paginated } from "./pagination";

// --- block descriptor (catalog) -------------------------------------------

export type BlockCategory = "source" | "transform" | "enricher" | "sink";

/** Config-field widget kinds — mirror of `hub.etl.descriptors.FieldType`. */
export type BlockFieldType =
  | "text"
  | "connection_string"
  | "secret"
  | "textarea"
  | "expression" // JS expression (QuickJS) — n8n ƒx
  | "sql"
  | "number"
  | "boolean"
  | "select"
  | "json"
  | "json_array"
  | "key_value"
  | "object_map"
  | "credential" // CredentialPicker (x-credential-type)
  | "dataset" // catalog-Dataset picker (stores the UUID)
  | "app_table" // two-level app→table picker (stores the table UUID)
  | "unique_set" // unique-set picker dependent on the sibling table_id
  | "canonical_model" // canonical-registry model picker
  | "blob" // file upload → stores the returned blob_ref
  | "template"; // per-item {{ }} template text (ƒx-aware in E2)

export interface BlockSelectOption {
  value: string;
  label: string;
}

export interface BlockVisibleWhen {
  dependsOn: string;
  value?: unknown;
}

export interface BlockConfigField {
  key: string;
  label: string;
  fieldType: BlockFieldType;
  required: boolean;
  defaultValue?: unknown;
  placeholder?: string;
  description?: string;
  options?: BlockSelectOption[];
  visibleWhen?: BlockVisibleWhen;
  /** For the `credential` field type — the credential type to filter. */
  credentialType?: string;
  /** Declared bounds for `number` fields (E1) — enforced on the input. */
  minValue?: number;
  maxValue?: number;
}

export interface BlockPort {
  id: string;
  label: string;
  color?: string;
  isAlwaysVisible: boolean;
}

export interface BlockDynamicPorts {
  sourceField: string;
  namePattern?: string;
  labelPattern?: string;
  channelProperty?: string;
}

export interface BlockUi {
  label: string;
  category: BlockCategory;
  icon: string;
  color: string;
}

export interface BlockDescriptor {
  type: string;
  ui: BlockUi;
  description: string;
  configFields: BlockConfigField[];
  inputs: BlockPort[];
  outputs: BlockPort[];
  dynamicInputs?: BlockDynamicPorts | null;
  dynamicOutputs?: BlockDynamicPorts | null;
  supportsPreview: boolean;
  /** A "blocking" operator (sort/aggregate/join): buffers its whole input
   *  before emitting, so downstream stalls until it finishes. */
  bufferAll?: boolean;
}

/** `{source: [...], transform: [...], enricher: [...], sink: [...]}`. */
export type BlockCatalog = Record<string, BlockDescriptor[]>;

/** Result of sampling a source block's first rows (the designer preview). */
export interface PreviewSourceResult {
  rows: Record<string, unknown>[];
  count: number;
  dead_lettered: number;
  truncated: boolean;
  duration_ms: number | null;
}

// --- pipeline definition (the designer document) --------------------------

export interface EtlNodeDef {
  id: string;
  type: string;
  label?: string;
  config: Record<string, unknown>;
  position?: { x: number; y: number };
}

export interface EtlEdgeDef {
  id?: string;
  from: string;
  to: string;
  channel?: string;
  sourceHandle?: string;
  targetHandle?: string;
}

export interface EtlPipelineDefinitionDoc {
  name: string;
  description?: string;
  /** Runtime knobs — maxDegreeOfParallelism / boundedCapacity / timeout /
   * sampleRows / persistSamples. Persisted in the version definition. */
  settings?: Record<string, unknown>;
  nodes: EtlNodeDef[];
  edges: EtlEdgeDef[];
  variables?: Record<string, unknown>;
}

// --- entity responses ------------------------------------------------------

/** Cron overlap policy: 'skip' (only when fully idle), 'coalesce' (at most one
 * pending queued — the default), 'allow' (always enqueue, bounded by a safety cap). */
export type OverlapPolicy = "skip" | "coalesce" | "allow";

export interface PipelineSummary {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  tags: string[];
  active_version_id: string | null;
  /** Scheduling (Phase A#1): cron (null = no schedule) + optional IANA timezone. */
  schedule_cron: string | null;
  schedule_timezone: string | null;
  /** What a due cron fire does when a previous run is still in flight (Phase A#1+). */
  schedule_overlap_policy: OverlapPolicy;
  /** Lane (corsia): FK to a predefined lane (null = no lane) + its name for display. */
  lane_id: string | null;
  lane_name: string | null;
  /** Queue priority 0–100 (higher runs first); default 50. */
  priority: number;
  created_at: string;
  updated_at: string | null;
}

export interface VersionSummary {
  id: string;
  version: number;
  notes: string | null;
  created_by: string;
  created_at: string;
}

export interface PipelineDetail extends PipelineSummary {
  settings: Record<string, unknown>;
  variables: Record<string, unknown>;
  /** The active version's definition — what the designer loads. */
  definition: EtlPipelineDefinitionDoc | null;
  versions: VersionSummary[];
}

export interface VersionDetail extends VersionSummary {
  pipeline_id: string;
  definition: EtlPipelineDefinitionDoc;
}

export type RunStatus =
  | "pending"
  | "running"
  | "cancelling"
  | "succeeded"
  | "failed"
  | "cancelled";

export interface RunDetail {
  id: string;
  pipeline_id: string;
  pipeline_version_id: string;
  status: RunStatus;
  mode: "sync" | "async";
  trigger: string;
  rows_in: number;
  rows_out: number;
  dead_lettered: number;
  node_metrics: Record<string, NodeMetricSnapshot> | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/** A run row in the cross-pipeline log (mirror of `GlobalRunSummary` in
 * `admin/routes/etl.py`). Lighter than `RunDetail` (no `node_metrics`) + the
 * pipeline NAME so the table needn't a second fetch. */
export interface GlobalRunSummary {
  id: string;
  pipeline_id: string;
  pipeline_name: string;
  status: RunStatus;
  mode: "sync" | "async";
  trigger: string;
  rows_in: number;
  rows_out: number;
  dead_lettered: number;
  /** Lane (corsia) name (null = no lane) + queue priority — surfaced in the
   * Esecuzioni › ETL tab so the operator sees the serialization domain + order. */
  lane_name: string | null;
  priority: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/** The SSE `etl_run_*` event shape (mirror of `hub.events.types.EtlRunEvent`),
 * as it arrives on the global live-event stream — used by the designer overlay. */
export interface EtlRunEventLike {
  kind: "etl_run_started" | "etl_run_finished" | "etl_run_metrics";
  run_id: string;
  pipeline_id: string;
  status?: string | null;
  rows_in?: number | null;
  rows_out?: number | null;
  dead_lettered?: number | null;
  node_metrics?: Record<string, NodeMetricSnapshot> | null;
}

/** One block's live counters — mirror of `runtime.BlockMetrics.snapshot()`.
 * `sample` / `dead_letter_sample` are the bounded first-N output rows captured
 * for the inspector (ADR-0039): present live (clamped over SSE) + on a reopened
 * run (persisted in `node_metrics`, unless `persist_samples=false`). */
export interface NodeMetricSnapshot {
  block_type?: string;
  items_in?: number;
  items_out?: number;
  out_by_channel?: Record<string, number>;
  dead_lettered?: number;
  errors?: number;
  duration_ms?: number | null;
  sample?: Array<Record<string, unknown>>;
  dead_letter_sample?: Array<Record<string, unknown>>;
}

// --- request bodies --------------------------------------------------------

/** A predefined ETL lane (corsia). */
export interface EtlLane {
  id: string;
  name: string;
  description: string | null;
  /** How many pipelines currently run in this lane. */
  pipeline_count: number;
  created_at: string;
}

export interface CreatePipelineBody {
  name: string;
  description?: string;
  tags?: string[];
  /** Optional FK to a predefined lane. */
  lane_id?: string | null;
  /** Queue priority 0–100 (higher runs first); default 50. */
  priority?: number;
  definition: EtlPipelineDefinitionDoc;
}

export interface UpdatePipelineBody {
  name?: string;
  description?: string;
  enabled?: boolean;
  tags?: string[];
  /** "" clears the schedule, omit leaves it untouched. */
  schedule_cron?: string;
  schedule_timezone?: string;
  /** Overlap policy on a due cron fire. Omit = untouched. */
  schedule_overlap_policy?: OverlapPolicy;
  /** Lane FK. Omit = untouched; null = clear; a uuid = assign. */
  lane_id?: string | null;
  /** Queue priority 0–100 (higher runs first). Omit = untouched. */
  priority?: number;
}

export interface CreateVersionBody {
  definition: EtlPipelineDefinitionDoc;
  notes?: string;
  activate?: boolean;
}

/** `POST /etl/uploads` result — the designer writes `blob_ref` into the config. */
export interface EtlUploadResult {
  blob_ref: string;
  filename: string;
  size: number;
  sha256: string;
}

// --- client ----------------------------------------------------------------

export const etlApi = {
  /** The designer palette source (descriptors grouped by category). */
  blocks: () => api.get<BlockCatalog>("/etl/blocks"),

  /** Sample the first N rows a source block would emit (designer preview). */
  previewSource: (body: {
    type: string;
    config: Record<string, unknown>;
    settings?: Record<string, unknown>;
    variables?: Record<string, unknown>;
    limit?: number;
  }) => api.post<PreviewSourceResult>("/etl/preview-source", body),

  /** Upload a source file (the BLOB picker) → the stored blob_ref. */
  uploadFile: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.upload<EtlUploadResult>("/etl/uploads", fd);
  },

  list: () => api.get<PipelineSummary[]>("/etl/"),
  get: (id: string) => api.get<PipelineDetail>(`/etl/${id}`),
  create: (body: CreatePipelineBody) => api.post<PipelineDetail>("/etl/", body),
  update: (id: string, body: UpdatePipelineBody) =>
    api.patch<PipelineDetail>(`/etl/${id}`, body),
  remove: (id: string) => api.delete<void>(`/etl/${id}`),

  /** Lanes (corsie) — predefined serialization domains. */
  lanes: {
    list: () => api.get<EtlLane[]>("/etl/lanes"),
    create: (body: { name: string; description?: string }) =>
      api.post<EtlLane>("/etl/lanes", body),
    update: (id: string, body: { name: string; description?: string }) =>
      api.patch<EtlLane>(`/etl/lanes/${id}`, body),
    remove: (id: string) => api.delete<void>(`/etl/lanes/${id}`),
  },

  versions: (id: string) => api.get<VersionSummary[]>(`/etl/${id}/versions`),
  getVersion: (versionId: string) =>
    api.get<VersionDetail>(`/etl/versions/${versionId}`),
  createVersion: (id: string, body: CreateVersionBody) =>
    api.post<VersionDetail>(`/etl/${id}/versions`, body),
  activate: (id: string, versionId: string) =>
    api.post<PipelineDetail>(`/etl/${id}/activate/${versionId}`),

  /** Run the active version. `wait=true` runs inline (bounded); else enqueued. */
  run: (id: string, wait = false) =>
    api.post<RunDetail>(`/etl/${id}/run`, undefined, { query: { wait } }),
  /** Debug run (ADR-0039 C4): caps every source to the first `rows` rows AND keeps
   * a sample that big, so a mapping can be inspected without streaming the origin.
   * Always runs inline. */
  runDebug: (id: string, rows = 100) =>
    api.post<RunDetail>(`/etl/${id}/run`, undefined, {
      query: { debug: true, debug_rows: rows },
    }),
  runs: (id: string, limit = 50) =>
    api.get<RunDetail[]>(`/etl/${id}/runs`, { query: { limit } }),
  /** ALL runs across every pipeline (the global "Esecuzioni · ETL" tab + the
   * Activity backfill). Paginated; each row carries the pipeline name. */
  allRuns: (params?: {
    page?: number;
    page_size?: 25 | 50 | 100;
    status?: RunStatus;
    pipeline_id?: string;
  }) => api.get<Paginated<GlobalRunSummary>>("/etl/runs", { query: params }),
  getRun: (runId: string) => api.get<RunDetail>(`/etl/runs/${runId}`),
  cancelRun: (runId: string) => api.post<RunDetail>(`/etl/runs/${runId}/cancel`),
};
